if (!customElements.get('product-sticky-bar')) {
  class ProductStickyBar extends HTMLElement {
    constructor() {
      super();
      this.sectionId = this.dataset.sectionId;
      this.targetSelector = this.dataset.target;
      this.variantInput = this.querySelector('input[name="id"]');
      this.priceEl = this.querySelector('[data-sticky-price]');
      this.compareEl = this.querySelector('[data-sticky-compare]');
      this.compareWrapper = this.querySelector('[data-sticky-compare-wrapper]');
      this.imageEl = this.querySelector('[data-sticky-image]');
       this.placeholderEl = this.querySelector('[data-sticky-placeholder]');
      this.button = this.querySelector('[data-sticky-atc-button]');
      this.buttonText = this.querySelector('[data-sticky-atc-button-text]');
      this.observer = null;
      this.variantChangeUnsub = null;
    }

    connectedCallback() {
      this.setupObserver();
      this.setupVariantListener();
      this.applyInitialState();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.variantChangeUnsub?.();
    }

    setupObserver() {
      // Find the product-form element (the actual add-to-cart form)
      // Look within the ProductInfo section for the product-form
      const productInfo = document.querySelector(this.targetSelector);
      if (!productInfo) return;

      // Find the product-form element within ProductInfo
      const productForm = productInfo.querySelector('product-form');
      if (!productForm) return;

      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Product form is visible - hide sticky bar
            this.classList.remove('product-sticky-atc--visible');
          } else {
            // Product form is scrolled out of view - show sticky bar
            // Calculate available screen space and set bottom position
            this.calculateAndSetBottom();
            this.classList.add('product-sticky-atc--visible');
          }
        });
      }, {
        threshold: 0,
        // Use negative rootMargin to trigger earlier (when form is about to leave viewport)
        rootMargin: '-20px 0px 0px 0px'
      });

      this.observer.observe(productForm);
    }

    setupVariantListener() {
      if (typeof subscribe === 'undefined' || typeof PUB_SUB_EVENTS === 'undefined') return;
      this.variantChangeUnsub = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
        if (!data?.variant || data.sectionId !== this.sectionId) return;
        this.updateFromVariant(data.variant);
      });
    }

    calculateAndSetBottom() {
      // Simple calculation: available screen height vs window height
      // This accounts for browser chrome, dev bars, etc.
      
      // Get the actual visible viewport height
      const visualViewport = window.visualViewport;
      const availableHeight = visualViewport?.height || document.documentElement.clientHeight || window.innerHeight;
      const windowHeight = window.innerHeight;
      
      // Calculate what's taking up space at the bottom
      // If window.innerHeight > availableHeight, there's something (browser chrome, dev bar, etc.)
      const bottomSpace = windowHeight - availableHeight;
      
      // Set bottom position to account for that space
      const bottomValue = Math.max(0, bottomSpace);
      this.style.bottom = `${bottomValue}px`;
    }

    applyInitialState() {
      const variant = {
        id: parseInt(this.dataset.initialVariantId || '0', 10),
        price: parseInt(this.dataset.initialPrice || '0', 10),
        compare_at_price: parseInt(this.dataset.initialComparePrice || '0', 10) || null,
        available: this.dataset.initialAvailable === 'true',
        featured_media: this.dataset.initialMedia
          ? {
              preview_image: {
                src: this.dataset.initialMedia,
                alt: this.dataset.initialMediaAlt || '',
              },
            }
          : null,
      };

      this.updateFromVariant(variant);
    }

    updateFromVariant(variant) {
      if (!variant || !this.variantInput) {
        this.setButtonState(true, window.variantStrings?.unavailable || 'Unavailable');
        return;
      }

      this.variantInput.value = variant.id || '';
      this.variantInput.disabled = !variant.available;

      this.renderPrice(variant.price, variant.compare_at_price);
      this.renderImage(variant.featured_media);

      if (variant.available) {
        this.setButtonState(false, window.variantStrings?.addToCart || this.dataset.ctaLabel || 'Add to cart');
      } else {
        this.setButtonState(true, window.variantStrings?.soldOut || this.dataset.soldOutLabel || 'Sold out');
      }
    }

    renderPrice(price, compareAtPrice) {
      if (this.priceEl) {
        this.priceEl.textContent = this.formatMoney(price || 0);
      }

      if (this.compareEl && this.compareWrapper) {
        const showCompare = compareAtPrice && compareAtPrice > price;
        this.compareWrapper.classList.toggle('hidden', !showCompare);
        if (showCompare) {
          this.compareEl.textContent = this.formatMoney(compareAtPrice);
        }
      }
    }

    renderImage(featuredMedia) {
      const src = featuredMedia?.preview_image?.src || this.dataset.initialMedia || '';

      if (this.imageEl) {
        if (src) {
          this.imageEl.src = src;
          this.imageEl.alt = featuredMedia?.preview_image?.alt || this.dataset.initialMediaAlt || '';
          this.imageEl.classList.remove('hidden');
        } else {
          this.imageEl.classList.add('hidden');
        }
      }

      if (this.placeholderEl) {
        this.placeholderEl.classList.toggle('hidden', !!src);
      }
    }

    setButtonState(disabled, label) {
      if (!this.button || !this.buttonText) return;
      if (disabled) {
        this.button.setAttribute('disabled', 'disabled');
      } else {
        this.button.removeAttribute('disabled');
      }
      this.buttonText.textContent = label;
    }

    formatMoney(cents) {
      if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
        return Shopify.formatMoney(cents, window.theme?.moneyFormat || window.Shopify?.money_format || '${{amount}}');
      }
      return new Intl.NumberFormat('da-DK', {
        style: 'currency',
        currency: 'DKK',
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format((cents || 0) / 100);
    }
  }

  customElements.define('product-sticky-bar', ProductStickyBar);
}

